import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, message, Typography, Tag, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { taskService, Task } from '../services/tasks';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

type FilterType = 'all' | 'ongoing' | 'complete';

export const Tasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'tasks.view');
  const canCreate = hasPermission(user, 'tasks.create');

  useEffect(() => {
    if (!canView) {
      setTasks([]);
      return;
    }
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await taskService.getAll();
      setTasks(response.data || []);
    } catch {
      message.error('Failed to load tasks data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await taskService.delete(id);
      message.success('Task deleted');
      fetchData();
    } catch {
      message.error('Failed to delete task');
    }
  };

  const formatTaskNumber = (id: number) => {
    return `#${id.toString().padStart(7, '0')}`;
  };

  const getReferenceLabel = (record: Task) => {
    if (record.type === 'business') {
      if (record.business?.name) return record.business.name;
      if (record.reference_id) return `Business #${record.reference_id}`;
      return '-';
    }
    if (record.type === 'personal') {
      if (record.customer?.name) return record.customer.name;
      if (record.reference_id) return `Customer #${record.reference_id}`;
      return '-';
    }
    return record.customer?.name || record.business?.name || '-';
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Plan':
        return 'blue';
      case 'Progress':
        return 'orange';
      case 'Review':
        return 'purple';
      case 'Approve':
        return 'cyan';
      case 'Completed':
        return 'green';
      default:
        return 'default';
    }
  };

  const filteredTasks = useMemo(() => {
    if (filter === 'all') {
      return tasks;
    } else if (filter === 'ongoing') {
      return tasks.filter(task => task.status && ['Plan', 'Progress', 'Review', 'Approve'].includes(task.status));
    } else if (filter === 'complete') {
      return tasks.filter(task => task.status === 'Completed');
    }
    return tasks;
  }, [tasks, filter]);

  const baseColumns = [
    {
      title: 'Task #',
      key: 'task_number',
      width: 120,
      sorter: (a: Task, b: Task) => a.id - b.id,
      render: (_: unknown, record: Task) => {
        const taskNumber = formatTaskNumber(record.id);
        return (
          <Tooltip title={taskNumber} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {taskNumber}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 180,
      sorter: (a: Task, b: Task) => getReferenceLabel(a).localeCompare(getReferenceLabel(b)),
      render: (_: any, record: Task) => (
        <Tooltip title={getReferenceLabel(record)} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getReferenceLabel(record)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 150,
      sorter: (a: Task, b: Task) => {
        const aType = a.taskType || (a as any).task_type;
        const bType = b.taskType || (b as any).task_type;
        const aName = aType?.name || '';
        const bName = bType?.name || '';
        return aName.localeCompare(bName);
      },
      render: (taskType: any, record: Task) => {
        // Backend returns 'task_type' (snake_case), but TypeScript interface uses 'taskType' (camelCase)
        const type = taskType || record.taskType || (record as any).task_type;
        const typeName = type?.name || '-';
        return (
          <Tooltip title={typeName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {typeName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Template',
      dataIndex: 'task_template',
      key: 'task_template',
      width: 180,
      sorter: (a: Task, b: Task) => {
        const aTemplate = a.taskTemplate || (a as any).task_template;
        const bTemplate = b.taskTemplate || (b as any).task_template;
        const aName = aTemplate?.name || '';
        const bName = bTemplate?.name || '';
        return aName.localeCompare(bName);
      },
      render: (taskTemplate: any, record: Task) => {
        // Backend returns 'task_template' (snake_case), but TypeScript interface uses 'taskTemplate' (camelCase)
        const template = taskTemplate || record.taskTemplate || (record as any).task_template;
        const templateName = template?.name || '-';
        return (
          <Tooltip title={templateName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {templateName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 100,
      sorter: (a: Task, b: Task) => (a.year || 0) - (b.year || 0),
      render: (year: number) => {
        const yearStr = year?.toString() || '-';
        return (
          <Tooltip title={yearStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {yearStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      sorter: (a: Task, b: Task) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      },
      render: (date: string) => {
        const dateStr = date ? new Date(date).toLocaleDateString() : '-';
        return (
          <Tooltip title={dateStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dateStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      sorter: (a: Task, b: Task) => (a.status || 'Plan').localeCompare(b.status || 'Plan'),
      render: (_: unknown, record: Task) => (
        <Tag color={getStatusColor(record.status)}>
          {record.status || 'Plan'}
        </Tag>
      ),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.key === 'task_number'
            ? (record: Task) => formatTaskNumber(record.id)
            : 'dataIndex' in col && col.dataIndex === 'customer'
            ? (record: Task) => record.customer?.name
            : 'dataIndex' in col && col.dataIndex === 'task_type'
            ? (record: Task) => {
                const type = record.taskType || (record as any).task_type;
                return type?.name || '';
              }
            : 'dataIndex' in col && col.dataIndex === 'task_template'
            ? (record: Task) => {
                const template = record.taskTemplate || (record as any).task_template;
                return template?.name || '';
              }
            : 'dataIndex' in col && col.dataIndex === 'year'
            ? (record: Task) => record.year
            : 'dataIndex' in col && col.dataIndex === 'due_date'
            ? (record: Task) => record.due_date
            : col.key === 'status'
            ? (record: Task) => record.status || 'Plan'
            : (undefined as unknown as (record: Task) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Task>(filteredTasks, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Tasks
        </Title>
        <Space>
          <ColumnVisibilityToggle
            columns={columnConfig}
            visibleKeys={visibleKeys}
            onChange={handleColumnVisibilityChange}
          />
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/tasks/new')}>
              New Task
            </Button>
          )}
        </Space>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            type={filter === 'ongoing' ? 'primary' : 'default'}
            onClick={() => setFilter('ongoing')}
          >
            Ongoing
          </Button>
          <Button 
            type={filter === 'complete' ? 'primary' : 'default'}
            onClick={() => setFilter('complete')}
          >
            Complete
          </Button>
        </Space>
      </div>

      <Table
        columns={columnsWithSearch}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ y: 'calc(100vh - 400px)' }}
        components={{
          header: {
            cell: ResizableTableHeader,
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          hideSelectAll: true,
          columnWidth: 0,
          renderCell: () => null,
        }}
        footer={() => (
          <div style={{ textAlign: 'left', padding: '8px 0' }}>
            Total: {filteredData.length} {filteredData.length === 1 ? 'task' : 'tasks'}
          </div>
        )}
        onRow={(record) => ({
          onClick: () => setSelectedRowKeys([record.id]),
          onDoubleClick: canView ? () => navigate(`/tasks/${record.id}/edit`) : undefined,
          style: { cursor: canView ? 'pointer' : 'default' },
        })}
      />
    </div>
  );
};
